import { useLanguage } from '@/i18n/LanguageContext'
import { useActivityLog } from '@/api/hooks/useAdmin'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Activity, MessageCircle, MessageSquare, AlertCircle, Info } from 'lucide-react'

export function Monitor() {
  const { t } = useLanguage()
  const { data: logs, isLoading } = useActivityLog()

  const typeConfig = {
    dm: { icon: MessageCircle, color: 'text-primary', bg: 'bg-primary/10', label: 'DM' },
    comment: { icon: MessageSquare, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/10', label: 'Comment' },
    error: { icon: AlertCircle, color: 'text-destructive', bg: 'bg-destructive/10', label: t('Erro') },
    info: { icon: Info, color: 'text-muted-foreground', bg: 'bg-muted', label: t('Info') },
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-40" />
        <div className="space-y-2">
          {[...Array(10)].map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-bold">Monitor</h1>
          </div>
          <p className="text-sm text-muted-foreground">{t('Atividade do sistema em tempo real (atualiza a cada 3s)')}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 animate-pulse rounded-full bg-primary" />
          <span className="text-xs text-muted-foreground">Live</span>
          <Badge variant="outline" className="text-[10px]">{logs?.length || 0} {t('entradas')}</Badge>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3">
        {Object.entries(typeConfig).map(([key, cfg]) => (
          <div key={key} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <cfg.icon className={`h-3 w-3 ${cfg.color}`} />
            {cfg.label}
          </div>
        ))}
      </div>

      {/* Log entries */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {!logs?.length ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Activity className="mb-2 h-10 w-10 text-muted-foreground/20" />
            <p className="text-xs text-muted-foreground">{t('Nenhuma atividade registrada')}</p>
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {logs.map((entry, i) => {
              const cfg = typeConfig[entry.type as keyof typeof typeConfig] || typeConfig.info
              const Icon = cfg.icon
              return (
                <div key={i} className="flex items-start gap-3 px-4 py-2.5 transition-colors hover:bg-muted/30">
                  <div className={`mt-0.5 shrink-0 rounded-md p-1 ${cfg.bg}`}>
                    <Icon className={`h-3 w-3 ${cfg.color}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs leading-relaxed text-foreground/90">{entry.message}</p>
                  </div>
                  <span className="shrink-0 text-[10px] text-muted-foreground/60">{entry.time}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
